import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchCategories } from '../services/categoriesService';
import { buildCategoryTree, isCategoryAncestorOf } from '../utils/categoryTreeUtils';
import { LIST_SORT_DEFAULT } from '../utils/contentListSort';
import { includesNormalized } from '../utils/searchHelpers';

const ROOT_KEY = '__ROOT__';

/**
 * Taşı: tek hedef kategori (accordion, Seç). onConfirm(null) = ana seviye / kategorisiz.
 */
export default function CategoryHedefSecModal({
  isOpen,
  onClose,
  mode,
  /** Ağaçta gösterilecek kategori tipi: ürün ağacı / parça ağacı vb. */
  kategoriTreeTipi,
  listSort = LIST_SORT_DEFAULT,
  sourceCategoryId = null,
  onConfirm,
  /** İçerik taşımada üstteki «Kategorisiz» kök hedefini gösterme (toplu kategorize vb.) */
  hideRootTarget = false,
  /** Opsiyonel başlık / alt başlık (örn. toplu taşıma) */
  customTitle,
  customSubtitle,
  /** Taşı butonu beklerken (örn. toplu API); iptal/kapat da kilitlenir */
  confirmBusy = false,
  /** İçerik taşımada hedef olamaz (mevcut kategori — toplu taşıma) */
  excludeCategoryId = null,
}) {
  const [flatCategories, setFlatCategories] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedKey, setSelectedKey] = useState(null);
  const [treeSearch, setTreeSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSelectedKey(null);
      setLoadError(null);
      setFlatCategories([]);
      setTreeSearch('');
      setTreeLoading(false);
      return;
    }
    let cancelled = false;
    setTreeLoading(true);
    (async () => {
      try {
        const res = await fetchCategories({ kategoriTipi: kategoriTreeTipi });
        const list = res?.veri?.kategoriler || res?.veri || [];
        if (!cancelled) {
          setFlatCategories(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Kategoriler yüklenemedi');
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, kategoriTreeTipi]);

  useEffect(() => {
    if (isOpen) {
      setSelectedKey(null);
      setExpandedIds(new Set());
      setTreeSearch('');
    }
  }, [isOpen, kategoriTreeTipi, sourceCategoryId]);

  const tree = useMemo(() => buildCategoryTree(flatCategories, listSort), [flatCategories, listSort]);

  const filterTreeByQuery = useCallback((nodes, q) => {
    const t = (q || '').trim();
    if (!t) return nodes;
    const walk = (nlist) => {
      if (!nlist || !nlist.length) return [];
      const out = [];
      for (const n of nlist) {
        const name = n.kategoriAdi || '';
        const ch = n.children ? walk(n.children) : [];
        if (includesNormalized(name, t) || ch.length) {
          out.push({ ...n, children: ch });
        }
      }
      return out;
    };
    return walk(nodes);
  }, []);

  const displayTree = useMemo(() => filterTreeByQuery(tree, treeSearch), [tree, treeSearch, filterTreeByQuery]);

  useEffect(() => {
    if (!treeSearch.trim() || !displayTree.length) return;
    const next = new Set();
    const collect = (nodes) => {
      nodes.forEach((n) => {
        const id = String(n.id || n._id);
        if (n.children && n.children.length) {
          next.add(id);
          collect(n.children);
        }
      });
    };
    collect(displayTree);
    setExpandedIds(next);
  }, [treeSearch, displayTree]);

  const rootLabel = mode === 'kategori' ? 'Ana seviye' : 'Kategorisiz';
  const rootHint = mode === 'kategori' ? 'Kök kategoriler ile aynı seviyede' : 'Hiçbir kategoriye bağlı değil';

  const isRowDisabled = useCallback(
    (categoryIdStr) => {
      if (mode === 'icerik' && excludeCategoryId && categoryIdStr === String(excludeCategoryId)) {
        return true;
      }
      if (mode !== 'kategori' || !sourceCategoryId) return false;
      const sid = String(sourceCategoryId);
      if (categoryIdStr === sid) return true;
      if (isCategoryAncestorOf(sid, categoryIdStr, flatCategories)) return true;
      return false;
    },
    [mode, sourceCategoryId, excludeCategoryId, flatCategories],
  );

  const toggleExpand = (idStr) => {
    setExpandedIds((prev) => {
      const n = new Set(prev);
      if (n.has(idStr)) n.delete(idStr);
      else n.add(idStr);
      return n;
    });
  };

  const handleConfirm = () => {
    if (confirmBusy) return;
    if (selectedKey == null) return;
    const target = selectedKey === ROOT_KEY ? null : selectedKey;
    if (hideRootTarget && mode === 'icerik' && target == null) return;
    const ret = onConfirm(target);
    if (ret != null && typeof ret.then === 'function') {
      ret.finally(() => onClose());
    } else {
      onClose();
    }
  };

  const title =
    customTitle || (mode === 'kategori' ? 'Kategoriyi taşı' : 'İçeriği taşı');
  const subtitle =
    customSubtitle ||
    (mode === 'kategori'
      ? 'Hedef üst grubu veya kök seviyeyi seçin'
      : hideRootTarget
        ? 'Hedef parça kategorisini seçin'
        : 'Hedef kategori veya kategorisiz alanı seçin');

  const renderNodes = (nodes, level) => {
    if (!nodes || !nodes.length) return null;
    return nodes.map((node) => {
      const id = node.id || node._id;
      const idStr = String(id);
      const hasCh = node.children && node.children.length > 0;
      const isExp = expandedIds.has(idStr);
      const disabled = isRowDisabled(idStr);
      const isSel = selectedKey === idStr;
      return (
        <div key={idStr} className="category-hedef-item">
          <div
            className={`category-hedef-row${isSel ? ' category-hedef-row--selected' : ''}${
              disabled ? ' category-hedef-row--disabled' : ''
            }`}
            style={{ paddingLeft: `${8 + level * 14}px` }}
          >
            {hasCh ? (
              <button
                type="button"
                className="category-toggle-btn category-hedef-chevron"
                disabled={confirmBusy}
                onClick={() => toggleExpand(idStr)}
                aria-expanded={isExp}
                aria-label={isExp ? 'Alt kategorileri gizle' : 'Alt kategorileri göster'}
              >
                <i className={`fas fa-chevron-${isExp ? 'down' : 'right'}`} />
              </button>
            ) : (
              <span className="category-toggle-spacer category-hedef-toggle-spacer" />
            )}
            <i className="fas fa-folder category-hedef-folder-icon" aria-hidden />
            <span className="category-hedef-name">{node.kategoriAdi}</span>
            <button
              type="button"
              className={`category-hedef-pick${isSel ? ' category-hedef-pick--active' : ''}`}
              disabled={disabled || confirmBusy}
              onClick={() => {
                if (disabled || confirmBusy) return;
                setSelectedKey(idStr);
              }}
            >
              {isSel ? <i className="fas fa-check" /> : null}
              {isSel ? 'Seçili' : 'Seç'}
            </button>
          </div>
          {hasCh && isExp && <div className="category-hedef-children">{renderNodes(node.children, level + 1)}</div>}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  const rootSelected = selectedKey === ROOT_KEY;
  return (
    <div
      className="modal-overlay category-hedef-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="category-hedef-title"
      onClick={(e) => {
        if (confirmBusy) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-content category-hedef-modal" onClick={(e) => e.stopPropagation()}>
        <div className="category-hedef-modal__header">
          <div className="category-hedef-modal__head-icon" aria-hidden>
            <i className="fas fa-sitemap" />
          </div>
          <div className="category-hedef-modal__head-text">
            <h2 id="category-hedef-title">{title}</h2>
            <p className="category-hedef-modal__subtitle">{subtitle}</p>
          </div>
          <button
            type="button"
            className="close-btn category-hedef-modal__close"
            onClick={onClose}
            aria-label="Kapat"
            disabled={confirmBusy}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="category-hedef-modal-body">
          {loadError && (
            <p className="form-error category-hedef-error" role="alert">
              {loadError}
            </p>
          )}

          {!(hideRootTarget && mode === 'icerik') && (
            <div
              className={`category-hedef-root-tile${rootSelected ? ' category-hedef-root-tile--selected' : ''}`}
              role="group"
              aria-label={mode === 'kategori' ? 'Ana seviye' : 'Kategorisiz hedef'}
            >
              <div className="category-hedef-root-tile__icon" aria-hidden>
                <i className={`fas ${mode === 'kategori' ? 'fa-layer-group' : 'fa-inbox'}`} />
              </div>
              <div className="category-hedef-root-tile__text">
                <span className="category-hedef-root-tile__title">{rootLabel}</span>
                <span className="category-hedef-root-tile__hint">{rootHint}</span>
              </div>
              <button
                type="button"
                className={`category-hedef-pick category-hedef-pick--root${rootSelected ? ' category-hedef-pick--active' : ''}`}
                disabled={confirmBusy}
                onClick={() => setSelectedKey(ROOT_KEY)}
              >
                {rootSelected ? <i className="fas fa-check" /> : null}
                {rootSelected ? 'Seçili' : 'Seç'}
              </button>
            </div>
          )}

          <div className="category-hedef-tree-box">
            <div className="category-hedef-tree-box-header">
              <i className="fas fa-code-branch" aria-hidden />
              <span>Kategori ağacı</span>
              <div className="category-hedef-tree-search">
                <i className="fas fa-search" aria-hidden />
                <input
                  type="search"
                  value={treeSearch}
                  onChange={(e) => setTreeSearch(e.target.value)}
                  disabled={confirmBusy}
                  placeholder="Ağaçta ara…"
                  autoComplete="off"
                  aria-label="Kategori adında ara"
                />
                {treeSearch ? (
                  <button
                    type="button"
                    className="category-hedef-tree-search-clear"
                    onClick={() => setTreeSearch('')}
                    aria-label="Aramayı temizle"
                  >
                    <i className="fas fa-times" />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="category-hedef-tree-scroll" role="tree">
              {treeLoading ? (
                <div className="category-hedef-loading" role="status" aria-live="polite">
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  <span>Ağaç yükleniyor…</span>
                </div>
              ) : !loadError && flatCategories.length === 0 ? (
                <p className="category-hedef-empty">
                  {hideRootTarget && mode === 'icerik'
                    ? 'Henüz parça kategorisi tanımlı değil. Önce Kategori Ekle ile kategori oluşturun.'
                    : `Henüz kategori tanımlı değil. Üstteki "${rootLabel}" seçilebilir.`}
                </p>
              ) : displayTree.length === 0 && treeSearch.trim() ? (
                <p className="category-hedef-empty">Aramanızla eşleşen kategori yok.</p>
              ) : (
                <div className="category-hedef-tree-inner">{renderNodes(displayTree, 0)}</div>
              )}
            </div>
          </div>
        </div>

        <div className="category-hedef-footer">
          <button
            type="button"
            className="secondary-btn category-hedef-btn-cancel"
            disabled={confirmBusy}
            onClick={onClose}
          >
            İptal
          </button>
          <button
            type="button"
            className="primary-btn category-hedef-btn-confirm"
            disabled={
              confirmBusy ||
              selectedKey == null ||
              (hideRootTarget && mode === 'icerik' && selectedKey === ROOT_KEY)
            }
            onClick={handleConfirm}
          >
            {confirmBusy ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden />
                Taşınıyor…
              </>
            ) : (
              <>
                <i className="fas fa-arrows-alt" aria-hidden />
                Taşı
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
